import { useMemo } from 'react';

import AssetsGraphIcon from '@atlaskit/icon-lab/core/assets-graph';
import AssetsObjectTypeIcon from '@atlaskit/icon-lab/core/assets-object-type';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import CurlyBracketsIcon from '@atlaskit/icon/core/curly-brackets';
import FlaskIcon from '@atlaskit/icon/core/flask';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getConfig } from '../../config';
import { useAtlassianUserContext } from '../../hooks/use-atlassian-user';

export type VisibleApp = {
	name: string;
	link: string;
	icon: React.ComponentType<NewCoreIconProps>;
};

const catalogApp: VisibleApp = {
	name: 'Catalog',
	link: '/catalog',
	icon: BookOpenIcon,
};

const allApps: VisibleApp[] = [
	{
		name: 'Assistant',
		link: '/assistant',
		icon: RovoIcon,
	},
	{
		name: 'Labs',
		link: '/labs',
		icon: FlaskIcon,
	},
	{
		name: 'Visualiser',
		link: '/visualiser',
		icon: AssetsObjectTypeIcon,
	},
];

const graphExplorerApp: VisibleApp = {
	name: 'Graph Explorer',
	link: '/graph-explorer',
	icon: AssetsGraphIcon,
};

const graphInferencePlatformApp: VisibleApp = {
	name: 'Graph Inference Platform',
	link: '/graphclaw-hub',
	icon: AiSparkleIcon,
};

const queryBuilderApp: VisibleApp = {
	name: 'Query Builder',
	link: '/query-builder',
	icon: CurlyBracketsIcon,
};

export type VisibleAppsResult = {
	apps: VisibleApp[];
	loading: boolean;
};

/**
 * Get the list of apps visible to the current user based on:
 * - Atlassian employees: see all apps
 * - Nonemployees: see Query Builder
 */
export function useVisibleApps(): VisibleAppsResult {
	const { identity, loading } = useAtlassianUserContext();
	const isLocalDevelopment = getConfig().isLocalDev;

	const apps = useMemo(() => {
		if (loading) {
			return [];
		}

		if (identity.isAtlassianEmployee || isLocalDevelopment) {
			const appsList = fg('twg-graphyte-gc-show-catalog') ? [catalogApp, ...allApps] : [...allApps];
			appsList.push(queryBuilderApp);
			if (fg('graph_explorer_in_super_app')) {
				appsList.push(graphExplorerApp);
			}
			appsList.push(graphInferencePlatformApp);
			return appsList;
		}

		return [queryBuilderApp];
	}, [identity.isAtlassianEmployee, isLocalDevelopment, loading]);

	return { apps, loading };
}
